import { useEffect } from 'react';
import { Title, Text, Group, Stack, Button, Collapse, Container } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { StatusOper, StatusType } from '../../../libraries/zps';
import { useSelector } from 'react-redux';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import ModalComponent from '../modal';
import {
  IconChevronUp,
  IconChevronDown,
  IconCircleCheckFilled,
  IconXboxXFilled,
  IconAlertCircleFilled
} from '@tabler/icons-react';
import './styles.scss';

interface StatusOperModalProps {
  opened: boolean;
  onClose: () => void;
  type: StatusType;
  message: string;
  httpStatus?: { code: number | string; message: string };
  statusOper?: StatusOper;
  error?: string;
  timeout?: number; // Optional timeout to auto-close the modal (in milliseconds)
}

const TYPE_CONFIG = {
  [StatusType.INFO]: {
    icon: IconCircleCheckFilled,
    className: 'status-ok',
    title: 'Info'
  },
  [StatusType.ERROR]: {
    icon: IconXboxXFilled,
    className: 'status-not-ok',
    title: 'Error'
  },
  [StatusType.WARNING]: {
    icon: IconAlertCircleFilled,
    className: 'alert-icon',
    title: 'Warning'
  }
};

const StatusOperModal = ({
  opened,
  onClose,
  type,
  message,
  httpStatus,
  statusOper,
  error,
  timeout
}: StatusOperModalProps) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const [detailsOpened, { toggle, close }] = useDisclosure(false);

  const resolvedType = type ?? StatusType.INFO;
  const config = TYPE_CONFIG[resolvedType];
  const IconComponent = config.icon;

  const showDetails = !!(httpStatus || statusOper || error);

  useEffect(() => {
    if (!opened) {
      close();
    }
  }, [opened, close]);

  useEffect(() => {
    if (!opened || !timeout) return;

    const timer = setTimeout(() => {
      onClose();
    }, timeout);

    return () => {
      clearTimeout(timer);
    };
  }, [opened, timeout, onClose]);

  const detailsTitle = resolvedType === StatusType.INFO ? lang.GLOBAL_SUCCESS_DETAILS : lang.GLOBAL_ERROR_DETAILS;

  return (
    <ModalComponent
      show={opened}
      handleClick={onClose}
      classname="status-oper-modal"
      content={
        <Container className="container-modal">
          {/* Header */}
          <Group className="icon-title" align="center" gap="sm">
            <IconComponent className={`${config.className} status-icon`} size="2.5em" />
            <Title>{message}</Title>
          </Group>

          {/* Details toggle */}
          {showDetails && (
            <Group onClick={toggle} className="collapsed-button">
              <Title>{detailsTitle}</Title>
              {detailsOpened ? <IconChevronUp size={25} /> : <IconChevronDown size={25} />}
            </Group>
          )}

          {/* Details content */}
          {showDetails && (
            <Collapse expanded={detailsOpened}>
              <Stack className="error-details">
                {/* HTTP status */}
                {httpStatus && (
                  <>
                    <Group className="error-details-info">
                      <Title>{`${lang.ALERT_HTTP_CODE}:`}</Title>
                      <Text>{`${httpStatus.code} ${httpStatus.message}`}</Text>
                    </Group>
                  </>
                )}

                {statusOper && (
                  <>
                    <Group className="error-details-info">
                      <Title>{`${lang.ALERT_ERROR_CODE}:`}</Title>
                      <Text>{statusOper.code}</Text>
                    </Group>
                    <Group className="error-details-info">
                      <Title>{`${lang.ALERT_ERROR_MESSAGE}:`}</Title>
                      <Text>{statusOper.message}</Text>
                    </Group>
                  </>
                )}

                {/* Exception details */}
                {error && (
                  <>
                    <Group className="error-details-info">
                      <Title>{`${lang.ALERT_EXCEPTION}:`}</Title>
                      <Text>{error}</Text>
                    </Group>
                  </>
                )}
              </Stack>
            </Collapse>
          )}

          {/* Footer */}
          <Group className="button-container">
            <Button radius="md" onClick={onClose}>
              OK
            </Button>
          </Group>
        </Container>
      }
    />
  );
};

export default StatusOperModal;
